import { IRoomSummary } from "@backend/src/shared/schemas/schemas";
import { IDevice } from "@multiconsult-group/multiplacer-core";
import { MCBimSpaceFillColors, PartialMcBimSpace } from "../utils";
import { ISpaceValidationRun } from "../DesignTab/designSlice";
import { ISelectedRoomsValidationMatrix } from "../DesignTab/devices/SelectedRooms/selectedRoomDevicesSelectors";
import { getPercentColor } from "./roomColoringValidation";
import { ROOM_COLORING_IDS, IRoomColoring, IPolygonColors } from "./roomColoringTypes";
import { spatialValidationsAreValid } from "../RoomSideBar/SpaceValidations/spatialValidationColoring";

export interface IArgGetColor {
  theSpaceNumber: string;
  activeSpace: {
    spaceNumber: string;
    spaceLongName: string;
  } | null;
  mcbimSpacesNameAndLongName: { spaceNumber: string; spaceLongName: string }[];
  selectedSpaces: string[];
  spaceValid: boolean | undefined;
  copySpaceModeActive: boolean;
  devicesExistsInSpace: boolean;
  spaceValidationRun: ISpaceValidationRun;
  activeRoomColoringId: ROOM_COLORING_IDS | string | undefined;
  room?: IRoomSummary | undefined;
  space?: PartialMcBimSpace | undefined;
  freeTextSearchHit: boolean;
  deviceOnTheMove?: IDevice;
  nonPublischedDevicesFromAlgosSpaceNumbers: string[];
  roomColorings: IRoomColoring[];
  spaceValidationsMatrixForSelectedRoomsExcludingStandardRoom: ISelectedRoomsValidationMatrix;
  runSpaceValidationsInSelectedRooms: boolean;
  spacesWithMcbimItemsHitsByFreeTextSearch: string[] | null;
  autoPipeRoutingIterationIsRunning?: boolean;
  copySpaceModePolygonMatchPct?: number | null;
}

const getBasePolygonColorsOfPixPolygonSpace = (args: IArgGetColor): IPolygonColors => {
  if (args.autoPipeRoutingIterationIsRunning) {
    return {};
  }

  if (args.deviceOnTheMove?.roomNumber === args.theSpaceNumber) {
    return {};
  }

  if (args.runSpaceValidationsInSelectedRooms) {
    const matrix = args.spaceValidationsMatrixForSelectedRoomsExcludingStandardRoom;
    const isSelected = (matrix?.rows ?? []).some((r) => r.roomNumber === args.theSpaceNumber);

    if (isSelected) {
      const row = (matrix.rows ?? []).find((r) => r.roomNumber === args.theSpaceNumber);
      if (!row || !row.hasDevices) {
        return {};
      }

      return {
        fill: spatialValidationsAreValid(row.validations)
          ? MCBimSpaceFillColors.GREEN
          : MCBimSpaceFillColors.RED,
      };
    }
  }

  if (args.nonPublischedDevicesFromAlgosSpaceNumbers.includes(args.theSpaceNumber)) {
    return { fill: MCBimSpaceFillColors.YELLOW };
  }

  if (args.copySpaceModeActive) {
    const mcbimSpace = args.mcbimSpacesNameAndLongName.find(
      (space) => space.spaceNumber === args.theSpaceNumber
    );
    if (mcbimSpace != null) {
      const longName = mcbimSpace.spaceLongName;
      if (args.activeSpace?.spaceLongName === longName) {
        return {
          fill:
            args.copySpaceModePolygonMatchPct != null
              ? getPercentColor(args.copySpaceModePolygonMatchPct)
              : MCBimSpaceFillColors.GREY,
        };
      }
    }
  }

  if (args.selectedSpaces.includes(args.theSpaceNumber ?? "")) {
    return { fill: MCBimSpaceFillColors.LIGHT_GRAY };
  }

  if (args.activeRoomColoringId != null) {
    const roomColoring = args.roomColorings.find((filter) => filter.id === args.activeRoomColoringId);
    if (!roomColoring) return {};

    return roomColoring.getPolygonColors({
      room: args.room as IRoomSummary,
      space: args.space,
      colorSpecs: roomColoring.colorSpecs,
    });
  }

  if (args.spaceValidationRun.active) {
    const spaceValidationResult = args.spaceValidationRun.spacesValidationsResults.find(
      (result) => result.spaceNumber === args.theSpaceNumber
    );
    if (spaceValidationResult != null) {
      const allValid = spatialValidationsAreValid(spaceValidationResult.spaceValidations);
      return {
        fill: allValid ? MCBimSpaceFillColors.GREEN : MCBimSpaceFillColors.RED,
      };
    }
    return {};
  }

  if (args.activeSpace?.spaceNumber === args.theSpaceNumber && args.devicesExistsInSpace) {
    if (args.spaceValid) {
      return { fill: MCBimSpaceFillColors.GREEN };
    }
    if (!args.spaceValid) {
      return { fill: MCBimSpaceFillColors.RED };
    }
  }

  if (
    args.theSpaceNumber === args.activeSpace?.spaceNumber &&
    args.theSpaceNumber === args.selectedSpaces[0] &&
    args.devicesExistsInSpace
  ) {
    if (args.spaceValid) {
      return { fill: MCBimSpaceFillColors.GREEN };
    }
    if (!args.spaceValid) {
      return { fill: MCBimSpaceFillColors.RED };
    }
  }

  return {};
};

/** Search highlights are overlays, never replacements for the room's data color. */
export const getPolygonColorsOfPixPolygonSpace = (args: IArgGetColor): IPolygonColors => {
  const colors = getBasePolygonColorsOfPixPolygonSpace(args);
  const searchHit = args.freeTextSearchHit || args.spacesWithMcbimItemsHitsByFreeTextSearch?.includes(args.theSpaceNumber);
  if (!searchHit || args.autoPipeRoutingIterationIsRunning) return colors;
  return {
    ...colors,
    outline: { color: "#1971c2", backgroundColor: "#ffffff", opacity: 1, width: 3, dashArray: [3, 2] },
  };
};

export const getFillColorOfPixPolygonSpace = (
  args: IArgGetColor
): MCBimSpaceFillColors | undefined => {
  const { fill } = getPolygonColorsOfPixPolygonSpace(args);
  return fill as MCBimSpaceFillColors | undefined;
};
